import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import cx from 'classnames';
import Styles from './index.module.scss';

export interface CommonTextareaExpose {
  insertText: (text: string, position: [number, number]) => void;
  getEscapedText: () => string;
  handleFocus: () => void;
}

export interface ICommonTextarea {
  className?: string;
  id: string;
  rows?: number;
  cols?: number;
  maxLength?: number;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  disabled?: boolean;
  readonly?: boolean;
  focused?: boolean;
  errorMsg?: string;
  wrap?: 'hard' | 'soft';
  autoResize?: boolean;
  enterCreate?: boolean; // 回车创建新 textarea
  isMaxLength?: boolean;
  onChange?: (contentId: string, val?: string, p?: [number, number]) => void;
  onFocus?: (textId: string) => void;
  onBlur?: (val?: string, p?: [number, number]) => void;
  onCreateNew?: (currentId: string, value: string) => void;
  onDelete?: (textId: string) => void;
}

const CommonTextarea = forwardRef<CommonTextareaExpose | undefined, ICommonTextarea>(function CommonTextarea ({
  id,
  className = '',
  defaultValue = '',
  value = '',
  disabled = false,
  focused = false,
  errorMsg = '',
  autoResize = false,
  enterCreate = false,
  maxLength = 10 * 1000,
  isMaxLength = false,
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  onCreateNew = () => {},
  onDelete = () => {},
  ...restProps
}, ref) {
  const [textareaValue, setTextareaValue] = useState(defaultValue || value);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const hiddenTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const heightRef = useRef<string>('auto');
  const [prevTextareaValue, setPrevTextareaValue] = useState(value);
  // 用于跟踪输入法组合输入状态
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    setTextareaValue(value);
    if (autoResize) {
      handleResize();
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (focused) {
      textareaRef.current?.focus();
    }
  }, [focused]);

  const handleResize = () => {
    setTimeout(() => {
      const textarea = textareaRef.current;
      const hiddenTextarea = hiddenTextareaRef.current;

      if (!textarea) {
        return;
      }
      textarea.style.height = heightRef.current;
      if (!hiddenTextareaRef) {
        return;
      }

      const height = hiddenTextarea?.scrollHeight;
      heightRef.current = `${height}px`;
      textarea.style.height = `${height}px`;
    }, 10);
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 仅在非输入法组合输入状态下且达到最大长度时阻止输入
    if (isMaxLength && !isComposing && e.key !== 'Backspace' && e.keyCode !== 8) {
      e.preventDefault();
      return;
    }
    if ((e.key === 'Enter' || e.keyCode === 13) && !e?.nativeEvent?.isComposing) {
      // 回车：创建新文本框
      onCreateNew(id, textareaValue);
    } else if ((e.key === 'Backspace' || e.keyCode === 8) && !textareaValue) {
      if (e.repeat) {
        // 回退键：若文本框为空，则删除该文本框
        onDelete(id);
      } else {
        setTimeout(() => {
          onDelete(id);
        }, 100)
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    // 仅在非输入法组合输入状态下且达到最大长度时阻止更改
    if (isMaxLength && !isComposing && val.length >= prevTextareaValue.length) {
      e.preventDefault();
      return;
    }
    if (val === '\n') {
      return ;
    }
    setTextareaValue(val);
    if (autoResize) {
      handleResize();
    }
    const position: [number, number] = [textareaRef.current?.selectionStart || 0, textareaRef.current?.selectionEnd || 0];
    onChange(id, val, position);
    setPrevTextareaValue(val);
  };

  const handleBlur = () => {
    textareaRef.current?.blur();
    const position: [number, number] = [textareaRef.current?.selectionStart || 0, textareaRef.current?.selectionEnd || 0];
    onBlur(textareaValue, position);
    onChange(id, textareaValue, position);
  };

  const handleFocus = () => {
    textareaRef.current?.focus();
    onFocus(id);
  };

  /**
   * 在指定范围内插入文本
   * @param text 待插入文本
   * @param p 位置范围
   */
  const insertText = (text: string, p = [0, 0]) => {
    textareaRef.current?.focus();
    textareaRef.current?.setRangeText(text, p[0], p[1], 'end')
    textareaRef.current?.dispatchEvent(new Event('change', { bubbles: true }))
  }

  /**
   * 获取转译后的内容
   * @returns
   */
  const getEscapedText = () => {
    return textareaRef.current?.innerHTML || ''
  }

  useImperativeHandle(ref, () => ({
    insertText,
    getEscapedText,
    handleFocus,
  }))

  return (
    <div className={cx(Styles.root, className, {
      [Styles.focused]: focused,
      [Styles.disabled]: disabled,
      [Styles.error]: textareaValue.length >= maxLength,
    })}>
      <textarea
        ref={textareaRef}
        className={Styles.textarea}
        onKeyDown={handleKeydown}
        onChange={handleChange}
        // 输入法组合输入开始时，设置 isComposing 为 true
        onCompositionStart={() => setIsComposing(true)}
        // 输入法组合输入结束时，设置 isComposing 为 false
        onCompositionEnd={() => setIsComposing(false)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        value={textareaValue}
        maxLength={maxLength}
        spellCheck="false"
        {...restProps}
      />
      {
        autoResize && <textarea ref={hiddenTextareaRef} className={Styles.hidden} rows={1} value={textareaValue} aria-hidden readOnly />
      }
      {
        textareaValue.length >= maxLength && <div className={Styles.error}>{errorMsg}</div>
      }
    </div>
  );
});

export default CommonTextarea;
